import React, { FC, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useAuth0 } from 'Auth';
import { useAgentPortalRoles } from 'Shared/hooks/useAgentPortalRoles';
import {
  useLazyIncentiveProgramsApi,
  useNewIncentiveProgramsApi,
} from 'Shared/services/Api';
import { useRewardsEnrollmentContext } from 'Shared/providers/RewardsEnrollmentProvider';
import { GetAgentPointsResponse } from 'Rewards/services/endpoints/GetAgentPoints/types';
import { AgentActivityResponse } from 'Rewards/services/endpoints/GetAgentActivity/types';
import { RewardActivity } from './RewardActivity';
import {
  entryThresholdFromConfig,
  maxWinPrizeForPhase,
  maxRafflePrizeForPhase,
} from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { PostAttemptResponse } from 'Rewards/services/endpoints/PostAttempt/types';
import { GetAgentRedemptionResponse } from 'Rewards/services/endpoints/PostRedeem/types';
import { RaffleEntryCardFull } from './RaffleEntryCard';
import { RaffleConfirmationModal } from './RaffleConfirmationModal';
import { reportEvent } from 'Shared/analytics';
import { env } from 'Shared/constants/env';
import { AnimatedScreen } from 'Shared/components';
import { BonusEntriesBadge } from 'Rewards/components/BonusEntriesBadge';
import styles from './styles.module.css';

export const TwentySixForTwentySix: FC<RouteComponentProps> = () => {
  const { user } = useAuth0();
  const history = useHistory();
  // Roles read once per session from currentUser GraphQL (Auth0 boot). No caching — satisfies AC5/AC6/AC7 real-time requirement.
  // isCarrierUser: internal Clearcover users (e.g. QA super users) always see the full producer view regardless of portal role assignment.
  const { hasAgentRole } = useAgentPortalRoles();
  const showProducerView = hasAgentRole || (user?.isCarrierUser ?? false);

  const {
    incentiveProgramEnrollmentResponse,
    refresh: refreshIncentiveProgramEnrollment,
    config,
    status,
    rewardsDataLoading,
    loadRewardsData,
    refetchStatus,
    setPendingAttempt,
  } = useRewardsEnrollmentContext();

  const isPendingTerms =
    incentiveProgramEnrollmentResponse?.status === 'TC_PENDING';

  // Fire the lazy provider loader on mount. Idempotent: if SpinWheel arrives
  // here first via deep-link, this is a no-op; otherwise this primes the
  // shared config/status so /rewards/spin reads them from context without
  // re-fetching.
  useEffect(() => {
    loadRewardsData();
  }, [loadRewardsData]);

  const [
    {
      data: pointsApiData,
      loading: pointsLoading,
      error: pointsError,
      refetch: refetchPoints,
    },
  ] = useNewIncentiveProgramsApi<GetAgentPointsResponse>(
    { url: `/agents/${user?.id}/points` },
    false,
    false,
    env.rewardsMockEnabled
  );
  const pointsData = env.rewardsMockEnabled
    ? { currentPoints: 350 }
    : pointsApiData
      ? { currentPoints: pointsApiData.balance }
      : null;

  // Reward Activity — fetched only for enrolled producers in live mode (AC1, DLC-459).
  // url is client-relative: the incentive-programs axios client already sets
  // baseURL to `${env.incentiveProgramsHostname}/incentive_programs`, so
  // `/agents/{id}/activity` resolves to the correct endpoint without double-prefixing.
  // In mock mode no live request is issued (provider contract: no API calls in mock).
  const [
    {
      data: activityData,
      loading: activityLoading,
      error: activityError,
      refetch: refetchActivity,
    },
  ] = useNewIncentiveProgramsApi<AgentActivityResponse>(
    { url: `/agents/${user?.id}/activity` },
    false,
    false,
    env.rewardsMockEnabled || !showProducerView // skip in mock mode or for non-producers who never see the section
  );

  const hasReportedView = useRef(false);
  useEffect(() => {
    if (!rewardsDataLoading && !hasReportedView.current && config && status) {
      hasReportedView.current = true;
      reportEvent('26for26-rewards-page:view');
    }
  }, [rewardsDataLoading, config, status]);

  // Separate dedup ref for the activity section view event (analytics-patterns.md).
  // Must NOT share hasReportedView — the two events track different data gates.
  // Guard on showProducerView: principal-only users never see <RewardActivity>
  // so the impression event must not fire for them.
  const hasReportedActivityView = useRef(false);
  useEffect(() => {
    if (
      showProducerView &&
      !activityLoading &&
      activityData &&
      !hasReportedActivityView.current
    ) {
      hasReportedActivityView.current = true;
      reportEvent('26for26-reward-activity:view');
    }
  }, [showProducerView, activityLoading, activityData]);

  // POST /attempts — fires on outer "SPIN NOW" click per journeys/03-agent-spins-the-wheel.
  // Creates a PENDING attempt and pre-determines the outcome. Entry stays
  // AVAILABLE until the inner Spin button on /rewards/spin fires PUT /accept.
  // Idempotent on the backend: if a PENDING attempt already exists, returns
  // the existing one with the same pre-determined outcome.
  const [, executeAttempt] = useLazyIncentiveProgramsApi<PostAttemptResponse>({
    method: 'POST',
    url: `/agents/${user?.id}/attempts`,
  });
  const [spinNowPending, setSpinNowPending] = useState(false);
  const [spinNowError, setSpinNowError] = useState(false);

  // ── Raffle entry state (AC2, AC3, AC5) ──────────────────────────────────────
  const [rafflePending, setRafflePending] = useState(false);
  const [raffleError, setRaffleError] = useState(false);
  const [raffleConfirmation, setRaffleConfirmation] = useState<{
    entryNumber: number;
    maxPrize: number | null;
  } | null>(null);
  // Capture the element that triggered the raffle modal so focus can be
  // returned to it when the modal closes (a11y — focus management).
  const raffleButtonRef = useRef<HTMLElement | null>(null);
  // Optimistic balance delta applied on successful raffle submission (AC3).
  // consumedBucket tells us which counter to decrement: BONUS first (AC8), then REGULAR.
  const [balanceDelta, setBalanceDelta] = useState<{
    regular: number;
    bonus: number;
  } | null>(null);

  const [{ loading: acceptLoading, error: acceptError }, executeAcceptTerms] =
    useLazyIncentiveProgramsApi<{ status: string }>({
      method: 'POST',
      url: `/agents/${user?.id}/accept-terms`,
    });

  const [{ loading: redeemLoading }, executeRedeem] =
    useLazyIncentiveProgramsApi<GetAgentRedemptionResponse>({
      method: 'GET',
      url: `/agents/${user?.id}/redeem`,
    });
  const [redeemError, setRedeemError] = useState(false);
  const [refreshError, setRefreshError] = useState(false);

  const handleAcceptTerms = () => {
    setRefreshError(false);
    executeAcceptTerms({
      data: { programCode: '26FOR26', termsVersion: 'v1.0' },
    })
      .then(() =>
        refreshIncentiveProgramEnrollment().catch(() => setRefreshError(true))
      )
      .catch(() => {});
  };

  const availableEntries = status?.availableEntries ?? 0;
  const canSpin = availableEntries > 0 && !spinNowPending;
  const policiesRemaining = status?.progress?.policiesUntilNextEntry ?? 0;
  const currentPhaseCode = status?.phase?.code ?? config?.phase?.code;
  const currentAttemptType =
    status?.phase?.attemptType ?? config?.phase?.attemptType;
  const isRafflePhase = currentAttemptType === 'RAFFLE_ENTRY';
  const entryThreshold = config ? entryThresholdFromConfig(config) : null;
  const maxPrize =
    config && currentPhaseCode
      ? maxWinPrizeForPhase(config, currentPhaseCode)
      : null;
  const maxRafflePrize =
    config && currentPhaseCode
      ? maxRafflePrizeForPhase(config, currentPhaseCode)
      : null;
  const filledSegments =
    entryThreshold != null
      ? Math.max(0, entryThreshold - policiesRemaining)
      : 0;

  // Build an optimistically-decremented status for the raffle card so the
  // displayed counts reflect the just-submitted entry (AC3). Only applied
  // after a successful submission (balanceDelta is set in handleEnterRaffle).
  const raffleDisplayStatus =
    status && balanceDelta
      ? {
          ...status,
          balance: {
            ...status.balance,
            regular: Math.max(0, status.balance.regular + balanceDelta.regular),
            bonus: Math.max(0, status.balance.bonus + balanceDelta.bonus),
          },
        }
      : status;

  const agentName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  const agentInitials = [user?.firstName?.[0], user?.lastName?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase();

  const handleSpinNow = () => {
    reportEvent('rewards-program:spin-now');
    setSpinNowError(false);

    if (env.rewardsMockEnabled) {
      history.push('/rewards/spin');
      return;
    }

    setSpinNowPending(true);
    executeAttempt({ data: {} })
      .then(({ data }) => {
        // Guard against an empty/null 2xx body — axios resolves on any 2xx
        // even when there's no usable response payload. Storing `undefined`
        // in context (typed as `PostAttemptResponse | null`) would mask the
        // failure and the wheel would render with no pending outcome.
        if (!data) {
          setSpinNowError(true);
          return;
        }
        // Seed the shared context with the PENDING attempt so the wheel
        // screen reads it directly — no /status refetch needed to learn
        // hasActiveAttempt/lastResult.
        setPendingAttempt(data);
        history.push('/rewards/spin');
      })
      .catch(() => {
        setSpinNowError(true);
      })
      .finally(() => setSpinNowPending(false));
  };

  const handleEnterRaffle = () => {
    // Capture the trigger for focus-return when the confirmation modal closes.
    raffleButtonRef.current = document.activeElement as HTMLElement;
    reportEvent('rewards-program:enter-raffle');
    setRaffleError(false);

    if (env.rewardsMockEnabled) {
      // Mock: show a synthetic confirmation so devs can see the modal in mock mode.
      // Decrement bonus first, then regular — mirrors AC8 / the real consumedBucket logic.
      setRaffleConfirmation({ entryNumber: 1001, maxPrize: maxRafflePrize });
      setBalanceDelta((prev) => {
        const bonusRemaining =
          (status?.balance.bonus ?? 0) + (prev?.bonus ?? 0);
        if (bonusRemaining > 0) {
          return prev
            ? { regular: prev.regular, bonus: prev.bonus - 1 }
            : { regular: 0, bonus: -1 };
        }
        return prev
          ? { regular: prev.regular - 1, bonus: prev.bonus }
          : { regular: -1, bonus: 0 };
      });
      return;
    }

    setRafflePending(true);
    // Disable the button immediately to prevent double-submit (premortem R-1).
    executeAttempt({ data: {} })
      .then(({ data }) => {
        if (!data) {
          reportEvent('rewards-program:enter-raffle-failure');
          setRaffleError(true);
          return;
        }
        if (data.raffleEntryNumber == null) {
          // Unexpected: entry number missing from response — surface as error rather
          // than silently showing a modal with no ID (would confuse the producer).
          reportEvent('rewards-program:enter-raffle-failure');
          setRaffleError(true);
          return;
        }
        if (data.consumedBucket == null) {
          // consumedBucket missing — we cannot determine which counter to decrement.
          // Trigger a status refetch so the displayed balance reflects server truth
          // rather than guessing the bucket and potentially double-decrementing the
          // wrong counter. Show the confirmation modal (the entry succeeded).
          refetchStatus().catch(() => {});
          setRaffleConfirmation({
            entryNumber: data.raffleEntryNumber,
            maxPrize: maxRafflePrize,
          });
          reportEvent('rewards-program:enter-raffle-success', {
            raffleEntryNumber: data.raffleEntryNumber,
            consumedBucket: 'unknown',
          });
          return;
        }
        // Optimistic decrement: use consumedBucket to know which counter went down (AC3/AC8).
        // Accumulate across multiple submissions on the same page view so N entries
        // correctly drive available counts down by N, not just 1.
        const delta =
          data.consumedBucket === 'BONUS'
            ? { regular: 0, bonus: -1 }
            : { regular: -1, bonus: 0 };
        setBalanceDelta((prev) =>
          prev
            ? {
                regular: prev.regular + delta.regular,
                bonus: prev.bonus + delta.bonus,
              }
            : delta
        );
        setRaffleConfirmation({
          entryNumber: data.raffleEntryNumber,
          maxPrize: maxRafflePrize,
        });
        reportEvent('rewards-program:enter-raffle-success', {
          raffleEntryNumber: data.raffleEntryNumber,
          consumedBucket: data.consumedBucket,
        });
      })
      .catch(() => {
        reportEvent('rewards-program:enter-raffle-failure');
        setRaffleError(true);
      })
      .finally(() => setRafflePending(false));
  };

  // When a fresh status arrives from the server the optimistic delta is no longer
  // needed — the server balance already reflects the submitted entry.  Reset here
  // so we don't double-subtract once the server returns the decremented balance.
  useEffect(() => {
    if (status) {
      setBalanceDelta(null);
    }
  }, [status]);

  const handleRaffleModalClose = () => {
    setRaffleConfirmation(null);
    // Trigger a status refetch so the balance is authoritative after modal close.
    // The useEffect above will reset balanceDelta once the fresh status arrives,
    // preventing double-decrement (optimistic delta + server-decremented balance).
    refetchStatus().catch(() => {});
    // Refetch activity so the newly submitted Pending row appears immediately (AC7).
    refetchActivity().catch(() => {});
  };

  const handleRedeemNow = () => {
    reportEvent('rewards-program:redeem-now');
    setRedeemError(false);

    if (env.rewardsMockEnabled) {
      window.open('https://app.awardco.com', '_blank', 'noreferrer');
      return;
    }

    // Open a blank window synchronously inside the user gesture so browsers
    // don't treat it as a popup. We navigate it to the SSO URL once the GET
    // resolves. noreferrer is omitted here because it implies noopener, which
    // causes window.open to return null — preventing the navigation below.
    const popup = window.open('', '_blank');

    executeRedeem()
      .then(({ data }) => {
        if (popup) {
          popup.location.href = data.redemptionUrl;
        }
      })
      .catch(() => {
        popup?.close();
        setRedeemError(true);
      });
  };

  return (
    <AnimatedScreen
      loading={rewardsDataLoading && !env.rewardsMockEnabled}
      testId="rewards-program-page"
      className={styles.screen}
    >
      {isPendingTerms ? (
        <div
          className={styles.termsGate}
          data-testid="rewards-program-terms-gate"
        >
          <div className={styles.termsContent}>
            <h1
              className={styles.termsTitle}
              data-testid="rewards-program-terms-title"
            >
              Welcome to 26 for '26!
            </h1>
            <p className={styles.termsBody}>
              We're offering rewards to thank our partners for choosing
              Clearcover. Every point you earn is worth $1 to spend on things
              you love.
            </p>
            <p className={styles.termsBodyBold}>
              Play a game, earn rewards, and compete for grand prizes.
            </p>
            <div className={styles.termsDivider} />
            <p className={styles.termsInstruction}>
              To get started with 26 for '26, please read and accept our Terms
              and Conditions
            </p>
            <a
              href="https://clearcover.com/26-for-26-terms" // TODO: update with final URL
              target="_blank"
              rel="noreferrer"
              className={styles.termsLink}
              data-testid="rewards-program-terms-link"
              onClick={() => reportEvent('26for26-terms:view-terms-link')}
            >
              Click here to view Terms & Conditions
            </a>
            <p className={styles.termsAgreeInstruction}>
              Once you have read and agreed to accept the terms, please click{' '}
              <strong>I Agree</strong> to continue to 26 for '26!
            </p>
            <button
              className={styles.termsAgreeButton}
              onClick={handleAcceptTerms}
              disabled={acceptLoading}
              data-testid="rewards-program-terms-agree-button"
            >
              {acceptLoading ? 'Processing...' : 'I Agree'}
            </button>
            {(acceptError || refreshError) && (
              <p
                className={styles.termsError}
                data-testid="rewards-program-terms-error"
              >
                Something went wrong. Please try again.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.pageWrapper}>
          <div className={styles.card} data-testid="rewards-program-card">
            {/* ── Card header ─────────────────────────────────────────────── */}
            <div className={styles.cardHeader}>
              <div className={styles.headerLeft}>
                <span className={styles.agentRewardsLabel}>AGENT REWARDS</span>
                <h1 className={styles.programTitle}>Clearcover 26 for '26</h1>
                <p className={styles.programSubtitle}>
                  Top agency by total sales wins
                </p>
              </div>
              <div className={styles.headerRight}>
                {agentName && (
                  <div className={styles.agentInfo}>
                    {agentInitials && (
                      <div className={styles.agentAvatar}>{agentInitials}</div>
                    )}
                    <div className={styles.agentDetails}>
                      <span
                        className={styles.agentName}
                        data-testid="rewards-program-agent-name"
                      >
                        {agentName}
                      </span>
                      {showProducerView && (
                        <span
                          className={styles.agentRole}
                          data-testid="rewards-program-agent-role"
                        >
                          Insurance Agent
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div className={styles.headerDivider} />
                <div
                  className={styles.pointsSection}
                  data-testid="rewards-program-points-balance"
                >
                  {pointsError ? (
                    <button
                      type="button"
                      className={styles.pointsRetry}
                      data-testid="rewards-program-points-error"
                      title="Couldn't load points. Click to retry."
                      onClick={() => {
                        // Safe to swallow: refetch calls the hook's execute, which
                        // keeps its error state set on failure, so pointsError stays
                        // truthy and this retry control stays visible for another try.
                        refetchPoints().catch(() => {});
                      }}
                    >
                      —
                    </button>
                  ) : pointsLoading ? (
                    <span
                      className={styles.pointsCount}
                      data-testid="rewards-program-points-loading"
                    >
                      …
                    </span>
                  ) : (
                    <span
                      className={styles.pointsCount}
                      data-testid="rewards-program-points-count"
                    >
                      {pointsData?.currentPoints != null
                        ? pointsData.currentPoints.toLocaleString()
                        : '—'}
                    </span>
                  )}
                  <span className={styles.pointsLabel}>POINTS AVAILABLE</span>
                </div>
                <button
                  className={styles.redeemButton}
                  onClick={handleRedeemNow}
                  disabled={redeemLoading}
                  data-testid="rewards-program-redeem-now"
                >
                  {redeemLoading ? 'LOADING…' : 'REDEEM NOW'}
                </button>
                {redeemError && (
                  <p
                    className={styles.redeemError}
                    data-testid="rewards-program-redeem-error"
                  >
                    Something went wrong. Please try again.
                  </p>
                )}
              </div>
            </div>

            {/* ── Inner content box (producer view only) ───────────────────── */}
            {showProducerView &&
              (isRafflePhase ? (
                <div className={styles.cardInner}>
                  <RaffleEntryCardFull
                    status={raffleDisplayStatus}
                    config={config}
                    submitPending={rafflePending}
                    onEnterRaffle={handleEnterRaffle}
                  />
                  {raffleError && (
                    <p
                      className={styles.spinError}
                      data-testid="rewards-program-raffle-error"
                    >
                      Something went wrong. Please try again.
                    </p>
                  )}
                </div>
              ) : (
                <div className={styles.cardInner}>
                  <div className={styles.innerRow}>
                    {/* Left: prize info */}
                    <div className={styles.prizeSection}>
                      <p className={styles.spinToWinLabel}>SPIN-TO-WIN</p>
                      <div className={styles.prizeRow}>
                        <span className={styles.prizeAmount}>
                          {maxPrize != null
                            ? `Win up to $${maxPrize}`
                            : 'SPIN-TO-WIN'}
                        </span>
                        <span className={styles.perSpin}>/spin</span>
                      </div>
                    </div>

                    {/* Right: entries + CTA */}
                    <div className={styles.ctaSection}>
                      <div
                        className={styles.entriesRow}
                        data-testid="rewards-program-entries"
                      >
                        <span className={styles.entriesCount}>
                          {availableEntries}
                        </span>
                        <span className={styles.entriesLabel}>
                          {availableEntries === 1 ? 'ENTRY' : 'ENTRIES'}{' '}
                          AVAILABLE
                          <br />
                          TO PLAY
                        </span>
                      </div>
                      <BonusEntriesBadge
                        phaseNumber={status?.phase?.number ?? 1}
                        bonusCount={status?.balance?.bonus ?? 0}
                      />
                      <button
                        className={styles.spinButton}
                        onClick={handleSpinNow}
                        disabled={!canSpin}
                        data-testid="rewards-program-spin-now"
                      >
                        {spinNowPending ? 'LOADING…' : 'SPIN NOW'}
                      </button>
                      {spinNowError && (
                        <p
                          className={styles.spinError}
                          data-testid="rewards-program-spin-error"
                        >
                          Something went wrong. Please try again.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Progress bar */}
                  {status?.progress && entryThreshold != null && (
                    <div className={styles.progressSection}>
                      <div className={styles.progressLabels}>
                        <span>NEXT ENTRY</span>
                        <span>
                          {policiesRemaining}{' '}
                          {policiesRemaining === 1 ? 'SALE' : 'SALES'} AWAY
                        </span>
                      </div>
                      <div
                        className={styles.progressBar}
                        data-testid="rewards-program-progress-bar"
                      >
                        {Array.from({ length: entryThreshold }).map((_, i) => (
                          <div
                            key={i}
                            className={
                              i < filledSegments
                                ? styles.progressSegmentFilled
                                : styles.progressSegmentEmpty
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>

          {/* ── Reward Activity section (enrolled producer only — AC1, DLC-459) ── */}
          {showProducerView && (
            <RewardActivity
              loading={activityLoading}
              data={activityData}
              error={activityError}
              onRetry={() => {
                // Safe to swallow: refetch keeps the hook's error state set on
                // failure, so activityError stays truthy and the retry stays shown.
                refetchActivity().catch(() => {});
              }}
            />
          )}
        </div>
      )}

      {/* ── Raffle confirmation modal (AC5) ────────────────────────────────── */}
      {raffleConfirmation && (
        <RaffleConfirmationModal
          raffleEntryNumber={raffleConfirmation.entryNumber}
          maxPrize={raffleConfirmation.maxPrize}
          onClose={handleRaffleModalClose}
          triggerRef={raffleButtonRef as React.RefObject<HTMLElement>}
        />
      )}
    </AnimatedScreen>
  );
};
